import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getCompanyIndustryProfiles } from '@/api/shared/industries'
import type { CompanyIndustryProfile, CompanyIndustryProfilesConfig } from '@/types/company'

/**
 * Company industry profiles served by the API (API/config/company_industry_profiles.php).
 * Decide which company profile sections and industry fields are shown for an industry.
 */
export const useCompanyIndustryProfileStore = defineStore('companyIndustryProfiles', () => {
  const config = ref<CompanyIndustryProfilesConfig | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  let pending: Promise<void> | null = null

  const isLoaded = computed(() => config.value !== null)

  const fetchProfiles = (force = false): Promise<void> => {
    if (config.value && !force) return Promise.resolve()
    if (pending) return pending

    isLoading.value = true
    error.value = null

    pending = getCompanyIndustryProfiles()
      .then(({ data }) => {
        config.value = data.data
      })
      .catch((err) => {
        console.error('Failed to fetch company industry profiles', err)
        error.value = 'Unable to load industry settings. Please refresh the page.'
      })
      .finally(() => {
        isLoading.value = false
        pending = null
      })

    return pending
  }

  // Mirrors CompanyIndustryProfiles::resolve(): first profile whose keyword is in the name.
  const resolve = (industryName: string | null | undefined): CompanyIndustryProfile | null => {
    if (!config.value) return null

    const profiles = config.value.profiles
    const needle = (industryName ?? '').trim().toLowerCase()

    if (needle) {
      const match = profiles.find((profile) =>
        profile.match.some((keyword) => keyword && needle.includes(keyword.toLowerCase()))
      )
      if (match) return match
    }

    return profiles.find((profile) => profile.key === config.value?.default) ?? null
  }

  return {
    config,
    isLoading,
    isLoaded,
    error,
    fetchProfiles,
    resolve,
  }
})
